import { usePwaStore } from '@/store/pwa'

/**
 * Notices when the deployed build stops matching the one this tab is running.
 *
 * The service worker is not enough on its own. It only covers users who have one
 * (registration is behind a feature flag, and it is skipped entirely outside
 * production), and it can do nothing about the layer above it: if the CDN serves
 * `index.html` with a long `max-age`, the browser never even asks for a new one, so
 * a tab can keep running a build from days ago and hitting API contracts and route
 * guards that have since changed. That looks to the user like the bug they reported
 * was never fixed.
 *
 * So compare a stamp instead. `version.json` is emitted per build (see the
 * `emit-app-version` plugin in vite.config.ts) and fetched with `cache: 'no-store'`,
 * which is one small request that cannot be answered from a stale cache. A mismatch
 * raises the existing "A new version is ready" prompt — see usePwaStore.
 */

/** Build stamp compiled into this bundle by the `emit-app-version` Vite plugin. */
declare const __APP_BUILD_ID__: string

const VERSION_MANIFEST_FILE = 'version.json'

/** Background cadence for a tab left open across a deploy. */
const CHECK_INTERVAL_MS = 30 * 60 * 1000

/** Floor between checks, so returning to a tab repeatedly doesn't re-poll each time. */
const MIN_CHECK_GAP_MS = 5 * 60 * 1000

const runningBuildId = (): string =>
  typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : ''

/** `version.json` next to the deployed assets, honouring any deploy base path. */
const versionManifestUrl = (): string => {
  const base = import.meta.env.BASE_URL || '/'
  const normalizedBase = base.endsWith('/') ? base : `${base}/`
  return new URL(
    `${normalizedBase}${VERSION_MANIFEST_FILE}`,
    window.location.origin,
  ).href
}

let lastCheckedAt = 0
let isWatching = false

const fetchDeployedBuildId = async (): Promise<string | null> => {
  try {
    // `no-store` handles the browser's own cache; the unique query gives an edge
    // cache a key it cannot already hold. A staleness check that can itself be
    // answered from a cache detects nothing, and this app has no say over the
    // response headers the host sends, so both belts are on deliberately.
    const response = await fetch(`${versionManifestUrl()}?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
    })
    if (!response.ok) return null

    const manifest = (await response.json()) as { buildId?: unknown }
    return typeof manifest.buildId === 'string' ? manifest.buildId : null
  } catch {
    // Offline, or the file isn't deployed yet — say nothing rather than nag.
    return null
  }
}

/**
 * One comparison against the deployed build.
 *
 * @returns whether a different build is live.
 */
export const checkForNewAppVersion = async (): Promise<boolean> => {
  const current = runningBuildId()
  if (!current) return false

  lastCheckedAt = Date.now()
  const deployed = await fetchDeployedBuildId()
  if (!deployed) return false

  const pwa = usePwaStore()
  if (deployed === current) {
    // This tab IS the deployed build — so if a reload was in progress, it landed.
    // Nothing else clears that flag when there is no service worker to report in,
    // and while it is set both the update and install prompts stay suppressed.
    pwa.finishApplyingUpdate()
    return false
  }

  pwa.onUpdateAvailable()
  return true
}

/**
 * Starts watching, on an interval and whenever the tab is brought back to the
 * foreground. Stops at the first mismatch — the prompt is up, and re-checking would
 * only re-raise a notification the user may have dismissed. Production only: there
 * is no `version.json` in a dev server.
 */
export const startAppVersionWatch = (): void => {
  if (isWatching || !import.meta.env.PROD || !runningBuildId()) return
  isWatching = true

  let intervalId = 0

  const stop = () => {
    window.clearInterval(intervalId)
    document.removeEventListener('visibilitychange', onVisible)
  }

  const check = () => {
    void checkForNewAppVersion().then((hasUpdate) => {
      if (hasUpdate) stop()
    })
  }

  function onVisible() {
    if (document.visibilityState !== 'visible') return
    if (Date.now() - lastCheckedAt < MIN_CHECK_GAP_MS) return
    check()
  }

  intervalId = window.setInterval(check, CHECK_INTERVAL_MS)
  document.addEventListener('visibilitychange', onVisible)

  // Check on load, not just after the first interval. A tab handed a cached
  // `index.html` is stale from the moment it opens — which is precisely the case
  // this exists for — and waiting half an hour to say so misses it entirely.
  check()
}
